import axios from "axios";
import {
  ApiClient,
  ApiError,
  CreateConnection,
  UpdateConnection,
  ForgotPasswordPayload,
  LoginPayload,
  SignupPayload,
  OpenAPIConnection,
  QueryGeneratePayload,
  QueryExecutePayload,
  ExtractVectorPayload,
  SaveQueryPayload,
  SavedQuery,
  ExtractSchemaPayload,
  CreateNotebookPayload,
  CreateCellPayload,
  UpdateCellPayload,
} from "./types";

const axiosApi = axios.create({
  baseURL: "https://becojyrxne.execute-api.eu-north-1.amazonaws.com/prod",
  timeout: 90000,
  headers: {
    "Content-Type": "application/json",
  },
});
axiosApi.interceptors.response.use(
  (response: any) => {
    if (import.meta.env.DEV) {
      console.log(
        `API Response: ${response.config.method?.toUpperCase()} ${
          response.config.url
        }`,
        response
      );
    }
    return response.data;
  },
  (error: any) => {
    const { response, request, config } = error;

    if (import.meta.env.DEV) {
      console.error(
        `API Error: ${config?.method?.toUpperCase()} ${config?.url}`,
        error
      );
    }

    if (response) {
      const { status, data } = response;
      const apiError: ApiError = {
        status,
        message: data?.message || `HTTP ${status} Error`,
        errors: data?.errors || [],
        data: data || null,
        config,
      };
      return Promise.reject(apiError);
    } else if (request) {
      const networkError: ApiError = {
        status: 0,
        message: "Network error - no response from server",
        errors: ["Please check your internet connection"],
        data: null,
        config,
      };
      return Promise.reject(networkError);
    } else {
      const configError: ApiError = {
        status: 0,
        message: error.message || "Request configuration error",
        errors: [error.message || "Unknown error"],
        data: null,
        config,
      };
      return Promise.reject(configError);
    }
  }
);

const api: ApiClient = {
  signup: async (payload: SignupPayload): Promise<any> => {
    return axiosApi.post("/signup", payload) as Promise<any>;
  },
  login: async (payload: LoginPayload): Promise<any> => {
    return axiosApi.post("/login", payload) as Promise<any>;
  },
  forgotPassword: async (payload: ForgotPasswordPayload): Promise<any> => {
    return axiosApi.post("/forgot-password", payload) as Promise<any>;
  },
  setAuthToken: (token: string | null) => {
    if (token) {
      axiosApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const currentStoredToken = localStorage.getItem("token");
      if (!currentStoredToken || currentStoredToken !== token) {
        // console.log("Setting auth token in localStorage");
      }
    } else {
      delete axiosApi.defaults.headers.common["Authorization"];
      // Only remove token if explicitly setting to null
      console.log("Clearing auth token from headers");
    }
  },
  clearAuth: () => {
    delete axiosApi.defaults.headers.common["Authorization"];
    console.log("Clearing auth data from axios headers");
  },
  createConnection: async (payload: CreateConnection) => {
    return axiosApi.post("/connections", payload) as Promise<any>;
  },
  getConnections: async (userId: string) => {
    return axiosApi.get(`/connections/${userId}`) as Promise<any>;
  },
  updateConnection: async (connectionId: string, payload: UpdateConnection) => {
    return axiosApi.put(
      `/connections/${connectionId}`,
      payload
    ) as Promise<any>;
  },
  deleteConnection: async (connectionId: string) => {
    return axiosApi.delete(`/connections/${connectionId}`) as Promise<any>;
  },
  testConnection: async (connectionId: string) => {
    return axiosApi.get(`/connections/${connectionId}/test`) as Promise<any>;
  },
  connectToDatabase: async (connectionId: string) => {
    return axiosApi.get(`/connections/${connectionId}/test`) as Promise<any>;
  },
  openAPIConnection: async (payload: OpenAPIConnection) => {
    return axiosApi.post("/open-ai/save-key", payload) as Promise<any>;
  },
  generateQuery: async (payload: QueryGeneratePayload) => {
    return axiosApi.post("/query-generator", {
      connection_id: payload.connection_id,
      query: payload.query,
    }) as Promise<any>;
  },
  executeQuery: async (payload: QueryExecutePayload) => {
    return axiosApi.post("/query-executor", {
      connection_id: payload.connection_id,
      query: payload.sql,
    }) as Promise<any>;
  },
  extractVector: async (payload: ExtractVectorPayload) => {
    return axiosApi.post("/extract-vector", payload) as Promise<any>;
  },
  saveQuery: async (payload: SaveQueryPayload) => {
    return axiosApi.post("/query", payload) as Promise<any>;
  },
  getSavedQueries: async (connectionId: string): Promise<SavedQuery[]> => {
    return axiosApi.get(`/query/${connectionId}`) as Promise<SavedQuery[]>;
  },
  extractSchema: async (payload: ExtractSchemaPayload) => {
    return axiosApi.get("/query/schema", {
      params: payload,
    }) as Promise<any>;
  },
  toggleFavorite: async (query_id: string): Promise<any> => {
    return axiosApi.patch(`/query/favorite`, { query_id }) as Promise<any>;
  },
  createNotebook: async (payload: CreateNotebookPayload): Promise<any> => {
    return axiosApi.post("/notebook", payload) as Promise<any>;
  },
  getNotebook: async (): Promise<any> => {
    return axiosApi.get(`/notebook`) as Promise<any>;
  },
  deleteNotebook: async (notebookId: string): Promise<any> => {
    return axiosApi.delete(`/notebook/${notebookId}`) as Promise<any>;
  },
  createCell: async (
    payload: CreateCellPayload,
    notebook_id: string
  ): Promise<any> => {
    return axiosApi.post(
      `/notebook/${notebook_id}/cell`,
      payload
    ) as Promise<any>;
  },
  instance: axiosApi,
  updateCell: async (payload: Partial<UpdateCellPayload>): Promise<any> => {
    return axiosApi.put(
      `/notebook/${payload.notebook_id}/cell/${payload.cell_index}`,
      payload
    ) as Promise<any>;
  },
  getAllCellsRelatedToNotebook: async (notebook_id: string): Promise<any> => {
    return axiosApi.get(`/notebook/${notebook_id}/cell`) as Promise<any>;
  },
  deleteCellCurrentNotebook: async (
    notebook_id: string,
    cell_index: number
  ): Promise<any> => {
    return axiosApi.delete(
      `/notebook/${notebook_id}/cell/${cell_index}`
    ) as Promise<any>;
  },
};
export default api;
export { axiosApi };
